import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Route to page title mapping
const routeTitles: Record<string, string> = {
  "/": "Chat",
  "/login": "Login",
  "/callback": "Authentication",
  "/signed-out": "Signed Out",
  "/blocked": "Access Blocked",
  "/admin": "Admin Dashboard",
  "/admin/glossary": "Admin Glossary",
  "/presence": "Presence",
  "/connect-poc": "Connect POC",
  "/gridlife": "Gridlife",
  "/internal-marker": "Internal Marker",
  "/testing": "Testing Hub",
  "/context-debug": "Context Debug",
  "/card-audit": "Card Audit",
  "/analytics-test": "Analytics Test",
  "/tdg-test": "TDG Test",
  "/tr-test": "TR Mock Test",
  "/mock-fixture-replay": "Fixture Replay",
  "/embed-timing-test": "Embed Timing Test",
  "/embed-gating-test": "Embed Gating Test",
};

// Routes with children of their own. Longest prefix wins, so a more specific
// exact entry above still takes precedence.
// NOTE: "/embed" is deliberately absent from both tables. Its title is the only
// one Adobe ever sees (pushToDataLayer only fires in embed mode), so naming it
// would move a live production pageName. See analytics.ts getCurrentPageName.
const routePrefixTitles: [string, string][] = [["/qa", "QA Dashboard"]];

// Get environment prefix for title (every environment, including prod)
const getEnvPrefix = (): string => {
  const envName = import.meta.env.VITE_ENV || "dev";
  return `[${envName.toLowerCase()}] `;
};

// Build full page title
const matchPrefixTitle = (pathname: string): string | undefined =>
  routePrefixTitles
    .filter(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    .sort((a, b) => b[0].length - a[0].length)[0]?.[1];

const getPageTitle = (pathname: string): string => {
  const pageTitle = routeTitles[pathname] || matchPrefixTitle(pathname) || "Page";
  const envPrefix = getEnvPrefix();
  return `${envPrefix}TRACI - ${pageTitle}`;
};

/**
 * Keeps document.title current on route changes.
 *
 * TRAC-790: this used to also push a page view into a data layer for our own Adobe
 * tag. We no longer carry a tag — the agent reports through the site's own Adobe —
 * and page views on tirerack.com are Adobe's to count, not ours to duplicate.
 */
export const usePageTracking = (): void => {
  const location = useLocation();

  useEffect(() => {
    document.title = getPageTitle(location.pathname);
  }, [location.pathname]);
};

export default usePageTracking;
